"use client"

import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import Image from "next/image"
import { sharedColors } from "ui/theme/colors"
import { extractProperty, getNonUmbracoUrl, Link } from "utils"

import { Desktop, Mobile } from "ui"
import BusAnimation from "./BusAnimation"
import Form from "./Form"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  padding: 64px 0 0;
  gap: 32px;
  width: 100%;
  min-height: 100vh;
  background-color: ${sharedColors.brandDark};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0;
    gap: 0;
    display: grid;
    grid-template-columns: 1fr 466px;
    grid-template-rows: minmax(100vh, auto);
  }
`

const MainContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 48px;
    padding-bottom: 48px;
    gap: 25px;
    overflow-y: auto;
  }
`

const TopContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 0 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 25px;
  }
`
const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
`

const StyledTypography = styled(Typography)`
  p {
    margin: 0;
  }
  text-box-trim: trim-both;
  text-box-edge: cap alphabetic;
`

const BottomContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 0 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 48px;
    gap: 24px;
  }
`

const BottomTextBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 100%;
    gap: 24px;
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const CardLayout = styled.div`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  height: 172px;
  width: 156px;
  align-items: center;
  text-align: center;
  gap: 12px;
  border-radius: 12px;
  border: 1px solid ${sharedColors.borderGray};
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 276.67px;
    width: 100%;
    flex-direction: row;
    gap: 16px;
    padding: 16px;
    align-items: center;
    text-align: unset;
    height: 80px;
  }
  ${(props) => props.theme.breakpoints.up(1920)} {
    max-width: 415px;
  }
`

const CardIconBox = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 40px;
    height: 40px;
  }
`

const LegalBox = styled.div`
  padding: 0 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const LandingFooter = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  padding: 6px 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  height: 72px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const ImageContainer = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
`

const WhiteLine = styled.div`
  width: 100%;
  background-color: white;
  height: 34px;
  position: absolute;
  bottom: 0;
  right: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`
const FooterText = styled(Typography)`
  line-height: 125% !important;
  font-weight: 700;
`

const TrimmedTypography = styled(Typography)`
  text-box-trim: trim-both;
  text-box-edge: cap alphabetic;
`
export default function DesignA({
  data,
  locale,
  tooltipText,
}: {
  data: any
  locale: string
  tooltipText: string
}) {
  const logo = extractProperty(data, "logo", true)
  const title = extractProperty(data, "title")
  const description = extractProperty(data, "description")
  const subTitle = extractProperty(data, "subTitle")
  const text = extractProperty(data, "text")
  const iconListBlock = extractProperty(data, "iconList") || []
  const iconList =
    iconListBlock.map((el: any) => ({
      icon: extractProperty(el, "icon"),
      text: extractProperty(el, "text"),
    })) || []
  const legalText = extractProperty(data, "legalText")
  const contactButton = extractProperty(data, "contactButton")?.[0]?.name
  const form = extractProperty(data, "form")
  const formTitle = extractProperty(form?.[0], "formTitle")
  const termsConfirmationText = extractProperty(
    form?.[0],
    "termsConfirmationText",
  )
  const scrollToForm = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const formElement = document.getElementById("form")
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }
  return (
    <StyledLayout>
      <MainContentLayout>
        <TopContentBox>
          <Mobile centerHorizontally>
            <Image
              alt={logo?.[0]?.altText || ""}
              src={getNonUmbracoUrl(logo?.[0]?.url)}
              width={64}
              height={64}
            />
          </Mobile>
          <Desktop centerHorizontally>
            <Image
              alt={logo?.[0]?.altText || ""}
              src={getNonUmbracoUrl(logo?.[0]?.url)}
              width={72}
              height={72}
            />
          </Desktop>
          <TitleBox>
            <TrimmedTypography variant="h1" color="white">
              {title}
            </TrimmedTypography>
            <StyledTypography
              component="div"
              variant="body1"
              color="white"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </TitleBox>
          <Mobile>
            <Button
              variant="white"
              size="large"
              href="#form"
              LinkComponent={Link}
              onClick={scrollToForm}
            >
              <Typography
                variant="body1"
                fontWeight={700}
                color={sharedColors.brandDark}
              >
                {contactButton}
              </Typography>
            </Button>
          </Mobile>
        </TopContentBox>
        <BusAnimation locale={locale} template="A" />
        <BottomContentBox>
          <BottomTextBox>
            <TrimmedTypography variant="h3" color="#F2D902">
              {subTitle}
            </TrimmedTypography>
            <Typography variant="body1" color="white">
              {text}
            </Typography>
          </BottomTextBox>
          <CardsGrid>
            {iconList.map((el: any) => (
              <CardLayout key={el.text}>
                <CardIconBox>
                  <Image src={el.icon} alt={""} fill />
                </CardIconBox>

                <Typography variant="body1" fontWeight={400} color="white">
                  {el.text}
                </Typography>
              </CardLayout>
            ))}
          </CardsGrid>
          <Desktop>
            <StyledTypography
              variant="body3"
              color="white"
              component={"div"}
              style={{ padding: "16px 0" }}
              dangerouslySetInnerHTML={{ __html: legalText || "" }}
            />
          </Desktop>
        </BottomContentBox>
      </MainContentLayout>

      <Form
        formTitle={formTitle}
        termsConfirmationText={termsConfirmationText}
        tooltipText={tooltipText}
      />

      <LegalBox>
        <StyledTypography
          variant="body3"
          color="white"
          component={"div"}
          dangerouslySetInnerHTML={{ __html: legalText || "" }}
        />
      </LegalBox>
      <LandingFooter>
        <FooterText
          variant="body2"
          color={sharedColors.brandDark}
          style={{ zIndex: 10 }}
        >
          אגד. מחברת את ישראל.
        </FooterText>
        <Image
          style={{ zIndex: 10 }}
          alt=""
          height={43}
          width={43}
          src={"/icons/logo2.svg"}
        />
        <ImageContainer>
          <Image
            src={"/icons/footerUnion.svg"}
            alt=""
            width={361}
            height={72}
          />
        </ImageContainer>
        <WhiteLine />
      </LandingFooter>
    </StyledLayout>
  )
}
